import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Package, Plus, Trash2, Save, Search, X, Pencil } from 'lucide-react';

/**
 * Управление справочником товаров.
 * Полные права (создать / изменить / удалить) — у super-admin.
 * Обычный organizer (canManage=false) может только просматривать список и добавлять
 * новые позиции — редактирование и удаление чужих товаров для него скрыто.
 */
export default function ProductsManager({ canManage = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {id, name, name_en} | {} = create

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products');
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(p => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (p.name || '').toLowerCase().includes(s) || (p.name_en || '').toLowerCase().includes(s);
  });

  const remove = async (p) => {
    if (!window.confirm(`Удалить «${p.name}» из справочника?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success('Удалено');
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="products-manager">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-200">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Поиск по названию (RU/EN)..." value={search}
            onChange={e => setSearch(e.target.value)} data-testid="products-search"
            className="pl-9 border-slate-300 rounded-sm" />
        </div>
        <Button onClick={() => setEditing({ name: '', name_en: '' })} data-testid="products-add"
          className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Добавить товар
        </Button>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 text-center">Загрузка...</div>}
      {!loading && filtered.length === 0 && (
        <div className="p-6 text-sm text-slate-400 text-center">Ничего не найдено</div>
      )}
      {!loading && filtered.map(p => (
        <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
          data-testid={`product-row-${p.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#0046AD] shrink-0" />
              <span className="font-semibold text-slate-900 truncate">{p.name}</span>
            </div>
            {p.name_en && (
              <div className="text-xs text-slate-500 truncate ml-6 mt-0.5">EN: {p.name_en}</div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {canManage && (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditing(p)}
                  data-testid={`product-edit-${p.id}`} className="rounded-sm text-xs">
                  <Pencil className="w-3 h-3 mr-1" /> Изменить
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(p)}
                  data-testid={`product-delete-${p.id}`}
                  className="rounded-sm text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      ))}

      {editing && <ProductEditModal product={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function ProductEditModal({ product, onClose, onSaved }) {
  const isCreate = !product.id;
  const [name, setName] = useState(product.name || '');
  const [nameEn, setNameEn] = useState(product.name_en || '');
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error('Название обязательно');
    setLoading(true);
    try {
      if (isCreate) {
        await api.post('/products', { name, name_en: nameEn });
        toast.success('Товар добавлен');
      } else {
        await api.put(`/products/${product.id}`, { name, name_en: nameEn });
        toast.success('Сохранено');
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-sm shadow-2xl w-full max-w-lg p-6 space-y-4"
        onClick={e => e.stopPropagation()} data-testid="product-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#0046AD]" /> {isCreate ? 'Новый товар' : 'Изменить товар'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-sm">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Название (RU)</Label>
          <Input value={name} onChange={e => setName(e.target.value)} autoFocus
            data-testid="product-edit-name" className="border-slate-300 rounded-sm" maxLength={300} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Название (EN) — для англоязычных поставщиков</Label>
          <Input value={nameEn} onChange={e => setNameEn(e.target.value)}
            placeholder="Sodium hypochlorite"
            data-testid="product-edit-name-en" className="border-slate-300 rounded-sm" maxLength={300} />
          <p className="text-[11px] text-slate-400">Используется в письмах участникам с lang_code=ENG.</p>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Отмена</Button>
          <Button onClick={save} disabled={loading} data-testid="product-edit-save"
            className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
            <Save className="w-4 h-4 mr-1.5" /> {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </div>
  );
}
